import { Box, Typography, Button, Container, Grid, useTheme, useMediaQuery } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

export default function HomeSection1() {
  const theme = useTheme();
  
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimate(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        color: 'white',
        minHeight: { xs: 'auto', md: 'auto' },
        py: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 },
        px: { xs: 1, sm: 1.5, md: 2, lg: 3, xl: 4 },
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {/* Background Animated Elements */}
      
      {/* Floating Circles */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          animation: 'float 6s ease-in-out infinite',
          zIndex: 1,
          opacity: animate ? 0.6 : 0,
          transition: 'opacity 1s ease',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: '15%',
          width: '120px',
          height: '120px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.08)',
          animation: 'float 8s ease-in-out infinite reverse',
          zIndex: 1,
          opacity: animate ? 0.5 : 0,
          transition: 'opacity 1s ease 0.5s',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '15%',
          left: '20%',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.12)',
          animation: 'float 7s ease-in-out infinite',
          zIndex: 1,
          opacity: animate ? 0.7 : 0,
          transition: 'opacity 1s ease 1s',
        }}
      />

      {/* Animated Squares */}
      <Box
        sx={{
          position: 'absolute',
          top: '60%',
          right: '8%',
          width: '40px',
          height: '40px',
          background: 'rgba(255, 255, 255, 0.06)',
          transform: 'rotate(45deg)',
          animation: 'rotate 12s linear infinite',
          zIndex: 1,
          opacity: animate ? 0.8 : 0,
          transition: 'opacity 1s ease 1.5s',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '30%',
          left: '15%',
          width: '30px',
          height: '30px',
          background: 'rgba(255, 255, 255, 0.1)',
          transform: 'rotate(45deg)',
          animation: 'rotate 15s linear infinite reverse',
          zIndex: 1,
          opacity: animate ? 0.6 : 0,
          transition: 'opacity 1s ease 2s',
        }}
      />

      {/* Wavy Lines */}
      <Box
        sx={{
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          height: '100px',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.03), transparent)',
          animation: 'wave 10s ease-in-out infinite',
          zIndex: 1,
          opacity: animate ? 1 : 0,
          transition: 'opacity 1s ease 0.8s',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '0',
          left: '0',
          right: '0',
          height: '80px',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.02), transparent)',
          animation: 'wave 12s ease-in-out infinite reverse',
          zIndex: 1,
          opacity: animate ? 1 : 0,
          transition: 'opacity 1s ease 1.2s',
        }}
      />

      {/* Particle Effects */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '2px',
          height: '2px',
          background: 'rgba(255, 255, 255, 0.8)',
          borderRadius: '50%',
          animation: 'particle 4s ease-in-out infinite',
          zIndex: 1,
          opacity: animate ? 1 : 0,
          transition: 'opacity 1s ease 0.3s',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '40%',
          left: '30%',
          width: '3px',
          height: '3px',
          background: 'rgba(255, 255, 255, 0.6)',
          borderRadius: '50%',
          animation: 'particle 6s ease-in-out infinite 1s',
          zIndex: 1,
          opacity: animate ? 1 : 0,
          transition: 'opacity 1s ease 0.6s',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '70%',
          right: '25%',
          width: '2px',
          height: '2px',
          background: 'rgba(255, 255, 255, 0.7)',
          borderRadius: '50%',
          animation: 'particle 5s ease-in-out infinite 2s',
          zIndex: 1,
          opacity: animate ? 1 : 0,
          transition: 'opacity 1s ease 0.9s',
        }}
      />

      {/* Gradient Orbs */}
      <Box
        sx={{
          position: 'absolute',
          top: '25%',
          right: '5%',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: `radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)`,
          filter: 'blur(20px)',
          animation: 'pulse 8s ease-in-out infinite',
          zIndex: 1,
          opacity: animate ? 1 : 0,
          transition: 'opacity 1s ease 1.8s',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          left: '10%',
          width: '150px',
          height: '150px',
          borderRadius: '50%',
          background: `radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)`,
          filter: 'blur(15px)',
          animation: 'pulse 10s ease-in-out infinite reverse',
          zIndex: 1,
          opacity: animate ? 1 : 0,
          transition: 'opacity 1s ease 2.1s',
        }}
      />

      {/* CSS Keyframes for animations */}
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            33% { transform: translateY(-20px) rotate(120deg); }
            66% { transform: translateY(-10px) rotate(240deg); }
          }
          
          @keyframes rotate {
            0% { transform: rotate(45deg); }
            100% { transform: rotate(405deg); }
          }
          
          @keyframes wave {
            0%, 100% { transform: translateX(-100%); }
            50% { transform: translateX(100%); }
          }
          
          @keyframes particle {
            0%, 100% { 
              transform: translateY(0px) scale(1);
              opacity: 0.8;
            }
            50% { 
              transform: translateY(-30px) scale(1.5);
              opacity: 0.3;
            }
          }
          
          @keyframes pulse {
            0%, 100% { 
              transform: scale(1);
              opacity: 0.5;
            }
            50% { 
              transform: scale(1.2);
              opacity: 0.8;
            }
          }
        `}
      </style>

      <Container 
        maxWidth={{xs: '100%', sm: '100%', md: '100%', lg: '100%', xl: false}}
        sx={{ 
          px: { xs: 0, sm: 0, md: 0, lg: 0, xl: 0 },
          height: '100%',
          maxWidth: '1800px',
        }}
      >
        <Grid 
          container 
          spacing={{ xs: 1, sm: 1.5, md: 2, lg: 2.5, xl: 3 }} 
          alignItems="center"
          sx={{ 
            minHeight: { xs: 'auto', md: 'auto' },
            py: 0,
          }}
        >
          
          {/* Left Content - Text Section */}
          <Grid 
            item 
            xs={12} 
            md={6} 
            order={{ xs: 2, md: 1 }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              px: { xs: 0.5, sm: 1, md: 1.5, lg: 2, xl: 2.5 }
            }}
          >
            <Box 
              sx={{ 
                position: 'relative', 
                zIndex: 2,
                textAlign: { xs: 'center', md: 'left' },
                px: { xs: 0.5, sm: 1, md: 1.5, lg: 2, xl: 2.5 }
              }}
            >
              {/* Main Heading */}
              <Typography
                variant="h1"
                sx={{
                  fontSize: { 
                    xs: '2rem', 
                    sm: '2.5rem', 
                    md: '3rem', 
                    lg: '3.5rem', 
                    xl: '4rem' 
                  },
                  fontWeight: 500,
                  lineHeight: { xs: 1.2, md: 1.1 },
                  mb: { xs: 1, sm: 1.5, md: 2, lg: 2.5, xl: 3 },
                  textShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  opacity: animate ? 1 : 0,
                  transform: animate ? 'translateY(0)' : 'translateY(30px)',
                  transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                  '& .highlight-blue': {
                    color: theme.palette.secondary.main,
                    fontWeight: 500,
                    opacity: animate ? 1 : 0,
                    transform: animate ? 'scale(1)' : 'scale(0.9)',
                    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.2s',
                    display: 'inline-block',
                  },
                  '& .highlight-gray': {
                    color: '#bddcfd',
                    fontWeight: 500,
                    opacity: animate ? 1 : 0,
                    transform: animate ? 'scale(1)' : 'scale(0.9)',
                    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.4s',
                    display: 'inline-block',
                  },
                  '& .highlight-sky': {
                    color: '#83e4fd',
                    fontWeight: 500,
                    opacity: animate ? 1 : 0,
                    transform: animate ? 'scale(1)' : 'scale(0.9)',
                    transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.6s',
                    display: 'inline-block',
                  }
                }}
              >
                Keeping track of your{' '}
                <span className="highlight-blue">medical records</span>{' '}
                <span className="highlight-gray">doesn't have to be</span>{' '}
                <span className="highlight-sky">so darn stressful.</span>
              </Typography>

              {/* Subtitle */}
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem', lg: '1.4rem', xl: '1.5rem' },
                  fontWeight: 400,
                  lineHeight: { xs: 1.4, md: 1.5 },
                  mb: { xs: 1.5, sm: 2, md: 2.5, lg: 3, xl: 3.5 },
                  opacity: animate ? 0.9 : 0,
                  transform: animate ? 'translateY(0)' : 'translateY(20px)',
                  transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.3s',
                  maxWidth: { xs: '100%', md: '500px' },
                  mx: { xs: 'auto', md: 0 }
                }}
              >
                Only FootprintID® lets you prepare, share, and protect your medical
                history with our secure, easy to use app.
              </Typography>

              {/* Mobile Video Box */}
              <Box
                sx={{
                  display: { xs: 'block', md: 'none' },
                  position: 'relative',
                  borderRadius: { xs: '16px', sm: '20px', md: '24px', lg: '28px', xl: '32px' },
                  overflow: 'hidden',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  width: { 
                    xs: '100%', 
                    sm: '90%', 
                    md: '95%', 
                    lg: '100%' 
                  },
                  maxWidth: { 
                    xs: '400px', 
                    sm: '500px', 
                    md: '550px', 
                    lg: '600px',
                    xl: '627px' 
                  },
                  height: { 
                    xs: '220px', 
                    sm: '250px', 
                    md: '300px', 
                    lg: '350px',
                    xl: '354px' 
                  },
                  aspectRatio: '16/9',
                  marginLeft: { xs: 'auto', md: '0' },
                  marginRight: { xs: 'auto', md: '0' },
                  mb: { xs: 1.5, sm: 2, md: 2.5, lg: 3, xl: 3.5 },
                  opacity: animate ? 1 : 0,
                  transform: animate ? 'scale(1)' : 'scale(0.95)',
                  transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.5s',
                }}
              >
                <object
                  id="video-container"
                  width="100%"
                  height="100%"
                  className="video-play"
                  data="https://www.youtube.com/v/P_r3Wm4UiQk?rel=0"
                  aria-labelledby="video-title"
                  style={{ 
                    borderRadius: 'inherit',
                    objectFit: 'cover'
                  }}
                />        
              </Box>

              {/* Desktop & Medium Screen Button */}
              <Box sx={{ 
                display: { md: 'block' },
                textAlign: { md: 'center', lg: 'left' },
                mt: { md: 1, lg: 1.5, xl: 2 },
                opacity: animate ? 1 : 0,
                transform: animate ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
                transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.7s',
              }}>
                <Button
                  component={Link}
                  to="/contactus"
                  variant="contained"
                  sx={{
                    background: theme.palette.info.main,
                    color: 'white',
                    px: { xs: 2, sm: 2.5, md: 3, lg: 3.5, xl: 4 },
                    py: '10px',
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem', lg: '1.3rem', xl: '1.4rem' },
                    fontWeight: 600,
                    borderRadius: '50px',
                    textTransform: 'none',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
                    transition: 'all 0.3s ease',
                    minWidth: { md: '26vw', lg: '26vw' },
                    width: '100%',
                    maxWidth: '400px',
                    '&:hover': {
                      background: theme.palette.info.dark,
                      transform: 'translateY(-2px) scale(1.02)',
                      color: 'white',
                      boxShadow: '0 12px 35px rgba(0,0,0,0.3)',
                    },
                    '&:focus': {
                      background: theme.palette.info.dark,
                      color: 'white',
                    }
                  }}
                >
                  Get in Touch
                </Button>
              </Box>
            </Box>
          </Grid>

          {/* Right Video Section */}
          <Grid 
            item 
            xs={12} 
            md={6} 
            order={{ xs: 1, md: 2 }}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              px: { xs: 0.5, sm: 1, md: 1.5, lg: 2, xl: 2.5 }
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                zIndex: 2,
                width: '100%'
              }}
            >
              <Box
                sx={{
                  display: { xs: 'none', md: 'block' },
                  position: 'relative',
                  borderRadius: { xs: '16px', sm: '20px', md: '24px', lg: '28px', xl: '32px' },
                  overflow: 'hidden',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  width: { 
                    xs: '100%', 
                    sm: '90%', 
                    md: '95%', 
                    lg: '100%' 
                  },
                  maxWidth: { 
                    xs: '400px', 
                    sm: '500px', 
                    md: '550px', 
                    lg: '600px',
                    xl: '627px' 
                  },
                  height: { 
                    xs: '220px', 
                    sm: '250px', 
                    md: '300px', 
                    lg: '350px',
                    xl: '354px' 
                  },
                  aspectRatio: '16/9',
                  mb: { xs: 1.5, sm: 2, md: 2.5, lg: 3, xl: 3.5 },
                  opacity: animate ? 1 : 0,
                  transform: animate ? 'scale(1) translateX(0)' : 'scale(0.9) translateX(30px)',
                  transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.4s',
                }}
              >
                <div id="video-container" className="video-responsive-container">
            <iframe
              className="video-play"
              src="https://www.youtube.com/embed/P_r3Wm4UiQk?rel=0"
              title="FootprintID Introduction"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              aria-labelledby="video-title"
            ></iframe>
          </div>    
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
